<template>
  <div>
    <v-layout
      v-if="commentLoading"
      row
      wrap
      justify-center
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-layout>
    <v-list
      v-else
      three-line
    >
      <span class="px-2 py-1">
        Comments
      </span>
      <v-divider />
      <v-container
        grid-list-xs
        class="pa-1"
      >
        <v-layout
          column
          wrap
        >
          <v-flex
            v-for="comment in sortedComments"
            :key="comment.id"
          >
            <v-card class="max-shadow">
              <v-card-title>
                <v-layout
                  row
                  wrap
                  align-center
                  justify-space-between
                >
                  <v-layout
                    row
                    wrap
                    align-center
                  >
                    <div class="px-2">
                      <avatar
                        :text="comment.author.name"
                        :size="38"
                        :src="comment.author.photo"
                      />
                    </div>
                    <div class="subheading font-weight-medium">
                      {{ comment.author.name }}
                    </div>
                  </v-layout>
                  <span>{{ formatTime(comment.commentedAt) }}</span>
                </v-layout>
              </v-card-title>
              <v-card-text class="py-0">
                <v-layout
                  row
                  wrap
                  fill-height
                  justify-center
                  align-center
                >
                  <v-flex xs11>
                    <div class="caption">
                      {{ comment.comment }}
                    </div>
                  </v-flex>
                  <v-flex xs1>
                    <v-icon @click="deleteComment(comment)">
                      delete
                    </v-icon>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
      <v-text-field
        v-model="newCommentText"
        :loading="addCommentLoading"
        class="pt-2"
        name="comment"
        label="Add Comment"
        outline
        :append-outer-icon="newCommentText ? 'send' : ''"
        clearable
        @click:append-outer="sendMessage"
      />
    </v-list>
    <v-dialog
      v-model="deleteDialog"
      persistent
      max-width="700px"
      transition="dialog-transition"
      lazy
    >
      <v-card class="max-shadow">
        <v-card-title class="headline">
          Delete {{ selectedComment.author.name }}'s comment?
        </v-card-title>
        <v-card-text class="subheading">
          Do you want to delete {{ selectedComment.author.name }}'s comment?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-spacer />
          <v-btn
            color="primary"
            flat
            @click="deleteDialog = false"
          >
            cancel
          </v-btn>
          <v-btn
            flat
            color="primary"
            @click="confirmDelete"
          >
            delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      :right="true"
      :timeout="4000"
      :top="true"
      :color="snackColor"
    >
      {{ snackText }}
    </v-snackbar>
  </div>
</template>
<script>
import Avatar from '@/components/common/Avatar';
import * as moment from 'moment';
import _get from 'lodash/get';
import firebase, { firestore, database } from '@/firebase';

export default {
  name: 'Comments',
  components: {
    avatar: Avatar
  },
  props: {
    eventId: {
      type: String,
      required: true,
      default: ''
    }
  },
  data() {
    return {
      commentLoading: false,
      addCommentLoading: false,
      comments: [],
      deleteDialog: false,
      selectedComment: {
        author: {}
      },
      newCommentText: '',
      snackColor: 'primary',
      snackText: '',
      snackbar: false
    };
  },
  computed: {
    role() {
      return _get(this.$store.state.profile, 'role');
    },
    cid() {
      return _get(this.$store.state.profile, 'cid');
    },
    sortedComments() {
      const { comments } = this;
      return comments.sort((a, b) => {
        return (
          new Date(b.commentedAt.toDate()) - new Date(a.commentedAt.toDate())
        );
      });
    },
    commentRef() {
      return firestore.collection('feed').doc(this.eventId);
    }
  },
  watch: {
    eventId: {
      handler(val) {
        if (!val) return;
        this.commentLoading = true;
        this.commentRef
          .collection('comments')
          .get()
          .then(comments => {
            const docs = [];
            comments.forEach(commentSnapshot => {
              docs.push({
                ...commentSnapshot.data(),
                ...{ id: commentSnapshot.id }
              });
            });
            this.comments = docs;
            // console.log(docs);
            this.commentLoading = false;
          });
      },
      immediate: true
    }
  },
  methods: {
    sendMessage() {
      const commentText = this.newCommentText.trim();
      if (!commentText) {
        this.snackColor = 'error';
        this.snackText = 'Please complete your comment to proceed further';
        this.snackbar = true;
        return;
      }
      this.addCommentLoading = true;
      const commentId = database.ref('/idGen').push().key;
      this.commentRef
        .collection('comments')
        .doc(commentId)
        .set({
          comment: commentText,
          author: {
            cid: this.$store.state.profile.cid || '',
            name: this.$store.state.profile.name,
            uid: this.$store.state.profile.uid || '',
            photo: this.$store.state.profile.photo || ''
          },
          commentedAt: firebase.firestore.FieldValue.serverTimestamp()
        })
        .then(() => {
          this.commentRef
            .collection('comments')
            .get()
            .then(comments => {
              const docs = [];
              comments.forEach(commentSnapshot => {
                docs.push({
                  ...commentSnapshot.data(),
                  ...{ id: commentSnapshot.id }
                });
              });
              this.comments = docs;
              this.addCommentLoading = false;
              this.newCommentText = '';
            });
        });
    },
    formatTime(timestamp) {
      const date = timestamp.toDate();
      const today = moment().startOf('day');
      return moment(date).isAfter(today)
        ? moment(date).fromNow()
        : moment(date).calendar(today, {
            lastWeek: 'DD MMM [at] hh:mm A',
            sameElse: 'DD MMM [at] hh:mm A'
          });
    },
    deleteComment(comment) {
      this.selectedComment = comment;
      this.deleteDialog = true;
    },
    confirmDelete() {
      this.commentRef
        .collection('comments')
        .doc(this.selectedComment.id)
        .delete()
        .then(() => {
          this.snackColor = 'primary';
          this.snackbarMessage = 'Deleted successfully';
          this.snackbar = true;
          this.deleteDialog = false;
          this.comments = this.comments.filter(
            comment => comment.id !== this.selectedComment.id
          );
        })
        .catch(() => {
          this.snackColor = 'error';
          this.snackbarMessage =
            'Something went wrong. Please try after sometime';
          this.snackbar = true;
          this.deleteDialog = false;
        });
    }
  }
};
</script>
